import { ComponentType } from 'react';

import { format } from 'date-fns';
import styled from 'styled-components';

import { Badge } from '@components/badge';
import { ButtonProps } from '@components/button';
import { Card } from '@components/card';
import { useDeleteModal } from '@components/generic-modals';
import { IconProps } from '@components/icons';
import { IconDecimalFloat } from '@components/icons/decimal-float';
import { IconEdit } from '@components/icons/edit';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconMove } from '@components/icons/move';
import { IconTextFormatNumber } from '@components/icons/text-format-number';
import { IconTrash } from '@components/icons/trash';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { fieldTypeIconMap } from '@components/text-editors/addons/field-mapping-type-maps';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { useDeleteDocumentTemplate, useUpdateDocumentTemplateStatus } from 'api/documents/hooks';
import { DocumentTemplate, DocumentTemplateFieldTypes } from 'api/documents/requests';
import { FavoriteMenuItem } from 'ui/authenticated/main/favorites/favorites';

import { useDocumentAddEditSidebar } from './add-edit-sidebar';
import { useMoveDocumentTemplateModal } from './modals/move';

export const TYPE_ICON_MAP: { [Key in DocumentTemplateFieldTypes]: ComponentType<IconProps> } = {
  text: fieldTypeIconMap['text'],
  paragraph: fieldTypeIconMap['paragraph'],
  number: fieldTypeIconMap['number'],
  email: fieldTypeIconMap['email'],
  phone: fieldTypeIconMap['phone'],
  date: fieldTypeIconMap['date'],
  select: fieldTypeIconMap['select'],
  file: fieldTypeIconMap['file'],
  decimal: fieldTypeIconMap['decimal'],
};

const SelectLabel = (label: string, badge: string) => (
  <Stack space={2} orientation="horizontal" justify="space-between" align="center">
    <Text font="regular14" text={label} />
    <Badge text={badge} />
  </Stack>
);

// The badge-decorated label is JSX, so the plain label doubles as filterText.
const badgedOption = <T extends string>({
  value,
  label,
  badge,
}: {
  value: T;
  label: string;
  badge: string;
}) => ({
  value,
  filterText: label,
  label: SelectLabel(label, badge),
});

const DATE_FORMAT_PATTERNS = [
  'dd/MM/yyyy',
  'dd/MMM/yyyy',
  'MM/dd/yyyy',
  'MMMM d, yyyy',
  'yyyy-MM-dd',
] as const;
export const DATE_FORMAT_OPTIONS = DATE_FORMAT_PATTERNS.map((pattern) =>
  badgedOption({ value: pattern, label: pattern, badge: format(new Date(), pattern) }),
);

export const DATE_LANGUAGE_OPTIONS: { value: string; label: string }[] = [
  { value: 'en_US.UTF-8', label: 'English (US)' },
  { value: 'en_GB.UTF-8', label: 'English (UK)' },
  { value: 'fr_FR.UTF-8', label: 'French (France)' },
  { value: 'de_DE.UTF-8', label: 'German (Germany)' },
  { value: 'es_ES.UTF-8', label: 'Spanish (Spain)' },
  { value: 'it_IT.UTF-8', label: 'Italian (Italy)' },
  { value: 'is_IS.UTF-8', label: 'Icelandic' },
  { value: 'nl_NL.UTF-8', label: 'Dutch (Netherlands)' },
  { value: 'pt_PT.UTF-8', label: 'Portuguese (Portugal)' },
  { value: 'pt_BR.UTF-8', label: 'Portuguese (Brazil)' },
  { value: 'zh_CN.UTF-8', label: 'Chinese (Simplified, China)' },
  { value: 'zh_TW.UTF-8', label: 'Chinese (Traditional, Taiwan)' },
  { value: 'ja_JP.UTF-8', label: 'Japanese' },
  { value: 'ko_KR.UTF-8', label: 'Korean' },
  { value: 'ar_EG.UTF-8', label: 'Arabic (Egypt)' },
  { value: 'ru_RU.UTF-8', label: 'Russian' },
  { value: 'tr_TR.UTF-8', label: 'Turkish' },
  { value: 'sv_SE.UTF-8', label: 'Swedish' },
  { value: 'fi_FI.UTF-8', label: 'Finnish' },
  { value: 'pl_PL.UTF-8', label: 'Polish' },
  { value: 'el_GR.UTF-8', label: 'Greek' },
  { value: 'he_IL.UTF-8', label: 'Hebrew (Israel)' },
  { value: 'hi_IN.UTF-8', label: 'Hindi (India)' },
  { value: 'th_TH.UTF-8', label: 'Thai (Thailand)' },
];

export type NumberFormatterType = 'comma' | 'period' | 'space';
export const useNumberFormatOptions = () => {
  const { t } = useScopedTranslate(locale);
  return [
    badgedOption<NumberFormatterType>({ value: 'comma', label: t('comma'), badge: '1,000' }),
    badgedOption<NumberFormatterType>({ value: 'period', label: t('period'), badge: '1.000' }),
    badgedOption<NumberFormatterType>({ value: 'space', label: t('space'), badge: '1 000' }),
  ];
};
export const DECIMAL_PLACES = Array.from({ length: 8 }, (_, idx) => {
  const value = idx + 1;
  return badgedOption({
    value: value.toString(),
    label: String(value),
    badge: `1.${'0'.repeat(value)}`,
  });
});

export type DecimalFormatterType = 'comma-period' | 'period-comma' | 'space-comma' | 'space-period';
export const useDecimalFormatOptions = (decimals: number) => {
  const { t } = useScopedTranslate(locale);
  return [
    badgedOption<DecimalFormatterType>({
      value: 'comma-period',
      label: t('commaPeriod'),
      badge: `1,000.${'0'.repeat(decimals)}`,
    }),
    badgedOption<DecimalFormatterType>({
      value: 'period-comma',
      label: t('periodComma'),
      badge: `1.000,${'0'.repeat(decimals)}`,
    }),
    badgedOption<DecimalFormatterType>({
      value: 'space-comma',
      label: t('spaceComma'),
      badge: `1 000,${'0'.repeat(decimals)}`,
    }),
    badgedOption<DecimalFormatterType>({
      value: 'space-period',
      label: t('spacePeriod'),
      badge: `1 000.${'0'.repeat(decimals)}`,
    }),
  ];
};

export const Container = styled.div(() => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)',
  gap: pxToRem(24),
}));

type DocumentActionsProps = {
  document: DocumentTemplate;
  variant?: ButtonProps['variant'];
  hasDelete?: boolean;
};

export const NumberExampleCard = ({
  formatter,
  isDecimal = false,
  fixedDecimals,
}: {
  formatter: NumberFormatterType | DecimalFormatterType;
  isDecimal?: boolean;
  fixedDecimals?: number;
}) => {
  const { t } = useScopedTranslate(locale);
  let value = '';
  let decimals = `${'0'.repeat(fixedDecimals ?? 0)}`;
  switch (formatter) {
    case 'comma':
      value = '3,456,000';
      break;
    case 'period':
      value = '3.456.000';
      break;
    case 'space':
      value = '3 456 000';
      break;
    case 'comma-period':
      value = `3,456,000.${decimals}`;
      break;
    case 'period-comma':
      value = `3.456.000,${decimals}`;
      break;
    case 'space-comma':
      value = `3 456 000,${decimals}`;
      break;
    case 'space-period':
      value = `3 456 000.${decimals}`;
      break;
  }

  return (
    <Card space={4} variant="orange" hasBorder={false}>
      <Stack space={2} orientation="horizontal" align="center" justify="space-between">
        <Stack space={2} orientation="horizontal" align="center">
          {isDecimal ? (
            <IconDecimalFloat color="elementOrange100" />
          ) : (
            <IconTextFormatNumber color="elementOrange100" />
          )}
          <Text font="semibold12" color="elementOrange200" text={value} />
        </Stack>
        <Text
          font="semibold12"
          color="elementOrange200"
          style={{ opacity: 0.5 }}
          text={`${t('example')}`}
        />
      </Stack>
    </Card>
  );
};

export const DocumentActions = ({
  document,
  variant = 'ghost',
  hasDelete = false,
}: DocumentActionsProps) => {
  const { t } = useScopedTranslate(locale);
  const showDeleteModal = useDeleteModal();
  const showMoveModal = useMoveDocumentTemplateModal();
  const showEditSidebar = useDocumentAddEditSidebar();
  const deleteMutation = useDeleteDocumentTemplate();
  const editMutation = useUpdateDocumentTemplateStatus();

  const handleActiveToggle = (value: boolean) => {
    if (!editMutation.isLoading) {
      editMutation.mutateAsync({
        shortId: document.shortId,
        status: value ? 'active' : 'draft',
      });
    }
  };

  const isUsedInJourney = document.numJourneys > 0;

  return (
    <Menu
      buttonProps={{
        iconLeft: <IconMoreVertical />,
        variant,
      }}
      placement="left-start"
    >
      <Menu.Item
        label={t('edit')}
        icon={<IconEdit />}
        onSelect={() =>
          showEditSidebar({
            document,
          })
        }
      />
      <Menu.Item
        label={t('move')}
        icon={<IconMove />}
        isDisabled={isUsedInJourney}
        tooltip={isUsedInJourney ? t('moveDocumentTooltip') : undefined}
        onSelect={() =>
          showMoveModal({
            documentTemplate: document,
          })
        }
      />
      <FavoriteMenuItem shortId={document.shortId} contentType="documenttemplate" />
      {hasDelete && (
        <Menu.Item
          variant="danger"
          label={t('core.delete')}
          icon={<IconTrash />}
          isDisabled={isUsedInJourney}
          tooltip={isUsedInJourney ? t('deleteDocumentTooltip') : undefined}
          onSelect={() =>
            showDeleteModal({
              title: t('deleteDocument'),
              description: t('areYouSureDelete', { name: document.name }),
              hasUserConfirmation: true,
              onConfirm: () => deleteMutation.mutate(document.shortId),
            })
          }
        />
      )}
      <Menu.Separator />
      <Menu.Switch
        label="Active"
        onChange={handleActiveToggle}
        value={document.status === 'active'}
      />
    </Menu>
  );
};

const locale = {
  en: {
    edit: 'Edit',
    move: 'Move to workspace',
    deleteDocument: 'Delete document template',
    areYouSureDelete: 'Are you sure you want to delete the template: {name}?',
    deleteDocumentTooltip: 'You cannot delete a document template that is used in a journey',
    moveDocumentTooltip: 'You cannot move a document template that is used in a journey',
    comma: 'Comma',
    period: 'Period',
    space: 'Space',
    commaPeriod: 'Comma, period',
    periodComma: 'Period, comma',
    spaceComma: 'Space, comma',
    spacePeriod: 'Space, period',
    example: 'Example',
  },
};
